import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Search,
  FileText,
  Calendar,
  User,
  Server,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  X
} from 'lucide-react';
import api from '../api/axiosConfig';
import Footer from '../components/Footer';

interface LogEntry {
  date_log: string;
  user: string;
  instance: string;
  status: string;
  description: string;
  [key: string]: any;
}

type SortKey = 'date_log' | 'user' | 'instance' | 'status' | 'description';
type SortDirection = 'asc' | 'desc';

interface ColumnWidth {
  date_log: number;
  user: number;
  instance: number;
  status: number;
  description: number;
}

const defaultColumnWidths: ColumnWidth = {
  date_log: 180,
  user: 200,
  instance: 180,
  status: 120,
  description: 300,
};

const Logs: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 15;
  const navigate = useNavigate();

  // Sorting state
  const [sortKey, setSortKey] = useState<SortKey>('date_log');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Column resizing state
  const [columnWidths, setColumnWidths] = useState<ColumnWidth>(defaultColumnWidths);
  const [resizing, setResizing] = useState<{ column: keyof ColumnWidth; startX: number; startWidth: number } | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const jwt = localStorage.getItem('jwt');
      if (!jwt) throw new Error('Not authenticated');
      const response = await api.get('/api/listUserInstanceLogs', {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      setLogs(response.data || []);
    } catch (err: any) {
      let message =
        err.response?.data?.ErrorMessage ||
        err.response?.data?.message ||
        'Failed to fetch logs.';
      if (err.message && message === 'Failed to fetch logs.') {
        message = err.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  // Filter and sort logs
  useEffect(() => {
    let result = [...logs];
    
    // Apply search filter
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (log) =>
          log.user?.toLowerCase().includes(s) ||
          log.instance?.toLowerCase().includes(s) ||
          log.status?.toLowerCase().includes(s) ||
          log.description?.toLowerCase().includes(s) ||
          log.date_log?.toLowerCase().includes(s)
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let aVal: any = a[sortKey];
      let bVal: any = b[sortKey];
      
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      if (sortKey === 'date_log') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    setFilteredLogs(result);
    setPage(0);
  }, [search, logs, sortKey, sortDirection]);

  // Sorting handler
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection(key === 'date_log' ? 'desc' : 'asc');
    }
  };

  // Column resizing handlers
  const handleResizeStart = (column: keyof ColumnWidth, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({
      column,
      startX: e.clientX,
      startWidth: columnWidths[column],
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizing) return;
      const diff = e.clientX - resizing.startX;
      const newWidth = Math.max(80, resizing.startWidth + diff);
      setColumnWidths((prev) => ({
        ...prev,
        [resizing.column]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    if (resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizing]);

  const getStatusClass = (status: string): string => {
    const s = status?.toLowerCase() || '';
    if (s.includes('success') || s.includes('complete') || s.includes('running')) {
      return 'running';
    }
    if (s.includes('fail') || s.includes('error') || s.includes('terminated')) {
      return 'terminated';
    }
    if (s.includes('stop') || s.includes('pending')) {
      return 'pending';
    }
    return 'pending';
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch {
      return dateString;
    }
  };

  const renderSortIcon = (key: SortKey) => {
    if (sortKey !== key) {
      return <ChevronUp size={14} className="sort-icon inactive" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp size={14} className="sort-icon active" />
      : <ChevronDown size={14} className="sort-icon active" />;
  };

  const totalPages = Math.ceil(filteredLogs.length / rowsPerPage);
  const paginatedLogs = filteredLogs.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <div className="instance-list-page">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-brand">
          <img src="/icon-60.png" alt="ecosystem.Ai" className="brand-logo-img" />
          <div className="brand-text">
            <h1 className="brand-title">Instance Logs</h1>
            <p className="brand-tagline">Activity and event history</p>
          </div>
        </div>
        <div className="dashboard-header-actions">
          <div className="header-search">
            <Search size={14} className="header-search-icon" />
            <input
              type="text"
              placeholder="Search logs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="header-search-input"
            />
            {search && (
              <button
                className="header-search-clear"
                onClick={() => setSearch('')}
                title="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <button
            className="header-action-btn"
            onClick={fetchLogs}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'spinning' : ''} />
          </button>
          <button className="secondary-button" onClick={() => navigate('/')}>
            <ArrowLeft size={16} />
            Back to Instances
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="list-content">
        {/* Error Alert */}
        {error && (
          <div className="alert alert-error" style={{ margin: '16px 24px' }}>
            {error}
          </div>
        )}

        {/* Stats Bar */}
        <div className="list-section-header">
          <div className="list-section-title">
            <FileText size={18} />
            <span>All Logs</span>
            <span className="list-count">{filteredLogs.length} entries</span>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="loading-state" style={{ padding: '64px 0' }}>
            <RefreshCw size={32} className="spinning" />
            <span>Loading logs...</span>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <FileText size={48} />
            </div>
            <h3 className="empty-state-title">No Logs Found</h3>
            <p className="empty-state-description">
              {search
                ? 'No logs match your search criteria. Try a different search term.'
                : 'There are no logs available yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="data-table-container resizable-table">
              <table className="data-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th style={{ width: columnWidths.date_log, minWidth: columnWidths.date_log }}>
                      <div className="th-content sortable" onClick={() => handleSort('date_log')}>
                        <div className="th-label">
                          <Calendar size={14} />
                          Date
                        </div>
                        {renderSortIcon('date_log')}
                      </div>
                      <div className="resize-handle" onMouseDown={(e) => handleResizeStart('date_log', e)} />
                    </th>
                    <th style={{ width: columnWidths.user, minWidth: columnWidths.user }}>
                      <div className="th-content sortable" onClick={() => handleSort('user')}>
                        <div className="th-label">
                          <User size={14} />
                          User
                        </div>
                        {renderSortIcon('user')}
                      </div>
                      <div className="resize-handle" onMouseDown={(e) => handleResizeStart('user', e)} />
                    </th>
                    <th style={{ width: columnWidths.instance, minWidth: columnWidths.instance }}>
                      <div className="th-content sortable" onClick={() => handleSort('instance')}>
                        <div className="th-label">
                          <Server size={14} />
                          Instance
                        </div>
                        {renderSortIcon('instance')}
                      </div>
                      <div className="resize-handle" onMouseDown={(e) => handleResizeStart('instance', e)} />
                    </th>
                    <th style={{ width: columnWidths.status, minWidth: columnWidths.status }}>
                      <div className="th-content sortable" onClick={() => handleSort('status')}>
                        <div className="th-label">Status</div>
                        {renderSortIcon('status')}
                      </div>
                      <div className="resize-handle" onMouseDown={(e) => handleResizeStart('status', e)} />
                    </th>
                    <th style={{ width: columnWidths.description, minWidth: columnWidths.description }}>
                      <div className="th-content sortable" onClick={() => handleSort('description')}>
                        <div className="th-label">Description</div>
                        {renderSortIcon('description')}
                      </div>
                      <div className="resize-handle" onMouseDown={(e) => handleResizeStart('description', e)} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLogs.map((log, index) => (
                    <tr key={index}>
                      <td>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                          {formatDate(log.date_log)}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontWeight: 500 }}>{log.user || '-'}</span>
                      </td>
                      <td>
                        <span style={{ color: 'var(--primary)' }}>{log.instance || '-'}</span>
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusClass(log.status)}`}>
                          {log.status || '-'}
                        </span>
                      </td>
                      <td>
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {log.description || '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <div className="pagination-info">
                  Page {page + 1} of {totalPages}
                </div>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Logs;
